import { update_product_amount } from "./products";

const GRID_CELL_MIN_WIDTH = 56;
const GRID_GAP = 6;
const GRID_ROW_HEIGHT = 46;
const VIRTUAL_BUFFER_ROWS = 3;

let modalSelection = new Set();
let confirmedSelection = [];
let reservedIds = [];
let blockedNumbers = new Set();
let gridBuilt = false;
let gridClickBound = false;
let initialized = false;
let finalizeInFlight = false;
let draftFilters = { availability: "all", rangeMin: 1, rangeMax: 1 };
let appliedFilters = { availability: "all", rangeMin: 1, rangeMax: 1 };
let poolMeta = { mode: "range", rangeMin: 1, rangeMax: 1, numbers: [], totalCount: 0 };
let virtualScrollBound = false;
let virtualRenderScheduled = false;
let gridResizeObserver = null;

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

function campaignId() {
  return document.getElementById("raffle-number-selection")?.dataset.campaignId;
}

function totalTickets() {
  const modal = document.getElementById("raffle-number-selection");
  return parseInt(modal?.dataset.totalTickets, 10) || 0;
}

function createDefaultFilters() {
  const max = totalTickets() || 1;
  return {
    availability: "all",
    rangeMin: 1,
    rangeMax: max,
  };
}

function preparePoolMeta(filters) {
  const rangeMin = filters.rangeMin;
  const rangeMax = filters.rangeMax;

  if (filters.availability === "available_only") {
    const numbers = [];
    for (let number = rangeMin; number <= rangeMax; number += 1) {
      if (!blockedNumbers.has(number)) numbers.push(number);
    }
    poolMeta = {
      mode: "list",
      rangeMin,
      rangeMax,
      numbers,
      totalCount: numbers.length,
    };
    return;
  }

  poolMeta = {
    mode: "range",
    rangeMin,
    rangeMax,
    numbers: [],
    totalCount: Math.max(0, rangeMax - rangeMin + 1),
  };
}

function getPoolNumber(index) {
  if (poolMeta.mode === "range") return poolMeta.rangeMin + index;
  return poolMeta.numbers[index];
}

function getGridColumnCount(grid) {
  const colWidth = GRID_CELL_MIN_WIDTH + GRID_GAP;
  return Math.max(1, Math.floor(grid.clientWidth / colWidth));
}

function updateRangeSliderUI() {
  const minInput = document.getElementById("raffle-range-min");
  const maxInput = document.getElementById("raffle-range-max");
  const minLabel = document.getElementById("raffle-range-min-label");
  const maxLabel = document.getElementById("raffle-range-max-label");
  const activeTrack = document.querySelector("#raffle-range-slider .raffle-range-slider__track-active");
  if (!minInput || !maxInput) return;

  const min = parseInt(minInput.min, 10) || 1;
  const max = parseInt(minInput.max, 10) || 1;
  const minVal = parseInt(minInput.value, 10);
  const maxVal = parseInt(maxInput.value, 10);
  const range = max - min || 1;

  if (minLabel) minLabel.textContent = formatNumberLabel(minVal);
  if (maxLabel) maxLabel.textContent = formatNumberLabel(maxVal);

  if (activeTrack) {
    const left = ((minVal - min) / range) * 100;
    const width = ((maxVal - minVal) / range) * 100;
    activeTrack.style.left = `${left}%`;
    activeTrack.style.width = `${width}%`;
  }
}

function syncDraftFromInputs() {
  const availability = document.querySelector('input[name="raffle-filter-availability"]:checked')?.value || "all";
  const minInput = document.getElementById("raffle-range-min");
  const maxInput = document.getElementById("raffle-range-max");

  draftFilters = {
    availability,
    rangeMin: parseInt(minInput?.value, 10) || 1,
    rangeMax: parseInt(maxInput?.value, 10) || totalTickets(),
  };
}

function syncInputsFromDraft() {
  const radio = document.querySelector(`input[name="raffle-filter-availability"][value="${draftFilters.availability}"]`);
  if (radio) radio.checked = true;

  const minInput = document.getElementById("raffle-range-min");
  const maxInput = document.getElementById("raffle-range-max");
  if (minInput) minInput.value = String(draftFilters.rangeMin);
  if (maxInput) maxInput.value = String(draftFilters.rangeMax);

  updateRangeSliderUI();
}

function resetFiltersToDefault() {
  const defaults = createDefaultFilters();
  draftFilters = { ...defaults };
  appliedFilters = { ...defaults };
  syncInputsFromDraft();
}

function onRangeMinInput() {
  const minInput = document.getElementById("raffle-range-min");
  const maxInput = document.getElementById("raffle-range-max");
  if (!minInput || !maxInput) return;

  let minVal = parseInt(minInput.value, 10);
  let maxVal = parseInt(maxInput.value, 10);

  if (minVal > maxVal) {
    maxInput.value = String(minVal);
    maxVal = minVal;
  }

  draftFilters.rangeMin = minVal;
  draftFilters.rangeMax = maxVal;
  updateRangeSliderUI();
}

function onRangeMaxInput() {
  const minInput = document.getElementById("raffle-range-min");
  const maxInput = document.getElementById("raffle-range-max");
  if (!minInput || !maxInput) return;

  let minVal = parseInt(minInput.value, 10);
  let maxVal = parseInt(maxInput.value, 10);

  if (maxVal < minVal) {
    minInput.value = String(maxVal);
    minVal = maxVal;
  }

  draftFilters.rangeMin = minVal;
  draftFilters.rangeMax = maxVal;
  updateRangeSliderUI();
}

async function applyFilters() {
  syncDraftFromInputs();
  appliedFilters = { ...draftFilters };
  setGridLoading(true);
  await new Promise(requestAnimationFrame);
  await rebuildPool();
}

async function clearFilters() {
  resetFiltersToDefault();
  setGridLoading(true);
  await new Promise(requestAnimationFrame);
  await rebuildPool();
}

function modalSelectionArray() {
  return [...modalSelection].sort((a, b) => a - b);
}

function parseReservedIds() {
  const raw = document.getElementById("reserved_raffle_number_ids")?.value || "";
  return raw.split(",").map((v) => parseInt(v, 10)).filter((n) => !isNaN(n) && n > 0);
}

function syncHiddenFields() {
  const idsField = document.getElementById("reserved_raffle_number_ids");
  const numbersField = document.getElementById("selected_raffle_numbers");
  const quantityField = document.getElementById("raffle_tickets_quantity");

  if (idsField) idsField.value = reservedIds.join(",");
  if (numbersField) numbersField.value = confirmedSelection.join(",");
  if (quantityField) quantityField.value = String(confirmedSelection.length);
}

function formatNumberLabel(number) {
  return new Intl.NumberFormat("es-CL").format(number);
}

function currentModalSelection() {
  const fromDom = [...document.querySelectorAll("#raffle-modal-selected-chips [data-number]")]
    .map((el) => parseInt(el.dataset.number, 10))
    .filter((n) => !isNaN(n));
  if (fromDom.length) modalSelection = new Set(fromDom);
  return modalSelectionArray();
}

function resetModalSelection() {
  modalSelection = new Set();
  renderModalSelectedChips();
}

function renderChip(number, { removable = false, container } = {}) {
  const chip = document.createElement("div");
  chip.className = "raffle-selected-chip";
  chip.dataset.number = String(number);

  const label = document.createElement("span");
  label.textContent = formatNumberLabel(number);
  chip.appendChild(label);

  if (removable) {
    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "raffle-selected-chip-remove";
    removeBtn.setAttribute("aria-label", `Quitar ticket ${number}`);
    removeBtn.innerHTML = "&times;";
    removeBtn.addEventListener("click", () => removeConfirmedNumber(number));
    chip.appendChild(removeBtn);
  }

  container.appendChild(chip);
}

function renderModalSelectedChips() {
  const container = document.getElementById("raffle-modal-selected-chips");
  if (!container) return;

  container.innerHTML = "";
  modalSelectionArray().forEach((number) => {
    const chip = document.createElement("div");
    chip.className = "raffle-selected-chip";
    chip.dataset.number = String(number);

    const label = document.createElement("span");
    label.textContent = formatNumberLabel(number);
    chip.appendChild(label);

    const removeBtn = document.createElement("button");
    removeBtn.type = "button";
    removeBtn.className = "raffle-selected-chip-remove";
    removeBtn.setAttribute("aria-label", `Quitar ticket ${number}`);
    removeBtn.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';
    removeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      toggleModalSelection(number);
    });
    chip.appendChild(removeBtn);

    container.appendChild(chip);
  });

  updateModalSummary();
}

function renderConfirmedDisplay() {
  const container = document.getElementById("selected-tickets-display");
  const section = document.getElementById("selected-tickets-section");
  if (!container) return;

  container.innerHTML = "";
  confirmedSelection.sort((a, b) => a - b).forEach((number) => {
    renderChip(number, { removable: true, container });
  });

  if (section) section.hidden = confirmedSelection.length === 0;
}

function updateGridCell(number) {
  const grid = document.getElementById("raffle-number-grid");
  if (!grid) return;

  const button = grid.querySelector(`[data-number="${number}"]`);
  if (!button) return;

  button.classList.remove(
    "raffle-number-chip--available",
    "raffle-number-chip--selected",
    "raffle-number-chip--blocked"
  );

  if (blockedNumbers.has(number)) {
    button.classList.add("raffle-number-chip--blocked");
    button.disabled = true;
  } else if (modalSelection.has(number)) {
    button.classList.add("raffle-number-chip--selected");
    button.disabled = false;
  } else {
    button.classList.add("raffle-number-chip--available");
    button.disabled = false;
  }
}

function refreshBlockedState() {
  if (!gridBuilt) return;
  if (appliedFilters.availability === "available_only") {
    preparePoolMeta(appliedFilters);
  }
  renderVirtualGrid();
}

function setGridLoading(loading) {
  const grid = document.getElementById("raffle-number-grid");
  if (!grid) return;

  grid.classList.toggle("raffle-number-grid--loading", loading);
  if (loading) {
    grid.innerHTML = '<div class="raffle-number-grid-loader" aria-hidden="true"></div>';
    gridBuilt = false;
  }
}

function bindGridClick() {
  if (gridClickBound) return;

  const grid = document.getElementById("raffle-number-grid");
  if (!grid) return;

  grid.addEventListener("click", (e) => {
    const button = e.target.closest(".raffle-number-chip--available, .raffle-number-chip--selected");
    if (!button || !grid.contains(button)) return;

    const number = parseInt(button.dataset.number, 10);
    if (!isNaN(number)) toggleModalSelection(number);
  });

  gridClickBound = true;
}

function createGridButton(number) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "raffle-number-chip";
  button.textContent = formatNumberLabel(number);
  button.dataset.number = String(number);

  if (blockedNumbers.has(number)) {
    button.classList.add("raffle-number-chip--blocked");
    button.disabled = true;
  } else if (modalSelection.has(number)) {
    button.classList.add("raffle-number-chip--selected");
  } else {
    button.classList.add("raffle-number-chip--available");
  }

  return button;
}

function renderVirtualGrid() {
  const grid = document.getElementById("raffle-number-grid");
  if (!grid) return;

  if (poolMeta.totalCount === 0) {
    grid.innerHTML = "";
    grid.classList.remove("raffle-number-grid--loading");
    return;
  }

  const columns = getGridColumnCount(grid);
  const totalRows = Math.ceil(poolMeta.totalCount / columns);
  const totalHeight = totalRows * GRID_ROW_HEIGHT;
  const scrollTop = grid.scrollTop;
  const viewportHeight = grid.clientHeight || 290;
  const startRow = Math.max(0, Math.floor(scrollTop / GRID_ROW_HEIGHT) - VIRTUAL_BUFFER_ROWS);
  const endRow = Math.min(
    totalRows,
    Math.ceil((scrollTop + viewportHeight) / GRID_ROW_HEIGHT) + VIRTUAL_BUFFER_ROWS
  );
  const startIndex = startRow * columns;
  const endIndex = Math.min(poolMeta.totalCount, endRow * columns);

  let virtual = grid.querySelector(".raffle-number-grid__virtual");
  let windowEl = grid.querySelector(".raffle-number-grid__window");

  if (!virtual) {
    grid.innerHTML = "";
    virtual = document.createElement("div");
    virtual.className = "raffle-number-grid__virtual";
    windowEl = document.createElement("div");
    windowEl.className = "raffle-number-grid__window";
    virtual.appendChild(windowEl);
    grid.appendChild(virtual);
  }

  virtual.style.height = `${totalHeight}px`;
  windowEl.style.transform = `translateY(${startRow * GRID_ROW_HEIGHT}px)`;

  windowEl.innerHTML = "";
  const fragment = document.createDocumentFragment();
  for (let i = startIndex; i < endIndex; i += 1) {
    fragment.appendChild(createGridButton(getPoolNumber(i)));
  }
  windowEl.appendChild(fragment);

  grid.classList.remove("raffle-number-grid--loading");
}

function scheduleVirtualRender() {
  if (virtualRenderScheduled) return;
  virtualRenderScheduled = true;
  requestAnimationFrame(() => {
    virtualRenderScheduled = false;
    if (gridBuilt) renderVirtualGrid();
  });
}

function bindVirtualScroll() {
  const grid = document.getElementById("raffle-number-grid");
  if (!grid || virtualScrollBound) return;

  grid.addEventListener("scroll", scheduleVirtualRender, { passive: true });

  if (typeof ResizeObserver !== "undefined") {
    if (gridResizeObserver) gridResizeObserver.disconnect();
    gridResizeObserver = new ResizeObserver(scheduleVirtualRender);
    gridResizeObserver.observe(grid);
  }

  virtualScrollBound = true;
}

async function rebuildPool() {
  preparePoolMeta(appliedFilters);

  const grid = document.getElementById("raffle-number-grid");
  if (!grid) return;

  grid.classList.remove("raffle-number-grid--loading");
  grid.scrollTop = 0;
  renderVirtualGrid();
  bindVirtualScroll();
  bindGridClick();
  gridBuilt = true;
}

function getAvailableInDraftRange() {
  syncDraftFromInputs();
  const { rangeMin, rangeMax } = draftFilters;
  const available = [];

  for (let number = rangeMin; number <= rangeMax; number += 1) {
    if (!blockedNumbers.has(number) && !modalSelection.has(number)) {
      available.push(number);
    }
  }

  return available;
}

function pickRandomAvailableInDraftRange() {
  const available = getAvailableInDraftRange();
  if (available.length === 0) return null;

  const index = Math.floor(Math.random() * available.length);
  return available[index];
}

function selectRandomTicket() {
  const number = pickRandomAvailableInDraftRange();
  if (number == null) return;

  toggleModalSelection(number);
}

function toggleModalSelection(number) {
  if (blockedNumbers.has(number)) return;

  if (modalSelection.has(number)) {
    modalSelection.delete(number);
  } else {
    modalSelection.add(number);
  }

  updateGridCell(number);
  renderModalSelectedChips();
}

function updateModalSummary() {
  const quantityEl = document.getElementById("raffle-modal-quantity");
  const totalEl = document.getElementById("raffle-modal-total");
  const modal = document.getElementById("raffle-number-selection");
  if (!quantityEl || !totalEl || !modal) return;

  const quantity = modalSelection.size;
  quantityEl.textContent = String(quantity);

  const finishBtn = document.getElementById("raffle-finish-selection-btn");
  if (finishBtn) finishBtn.disabled = quantity === 0;

  if (quantity === 0) {
    totalEl.textContent = "$0";
    return;
  }

  const currency = document.getElementById("currency-type")?.value || "clp";
  const price = currency === "usd"
    ? parseFloat(modal.dataset.ticketPriceUsd) || 0
    : parseFloat(modal.dataset.ticketPrice) || 0;
  const total = price * quantity;

  totalEl.textContent = currency === "clp"
    ? `$${new Intl.NumberFormat("es-CL").format(total)}`
    : `USD ${total.toString().replace(".", ",")}`;
}

function updateConfirmedTotal() {
  const quantity = confirmedSelection.length;
  if (quantity === 0) {
    update_product_amount(0);
    return;
  }

  const currency = document.getElementById("currency-type")?.value || "clp";
  $.ajax({
    url: "/calculate_raffle_price",
    method: "GET",
    data: {
      campaign_id: campaignId(),
      quantity: quantity,
      currency: currency,
    },
    success(response) {
      let total = response.price;
      if (currency === "usd") {
        total = total.toString().replace(".", ",");
      }
      update_product_amount(total);
    },
    error() {
      const modal = document.getElementById("raffle-number-selection");
      const price = currency === "usd"
        ? parseFloat(modal?.dataset.ticketPriceUsd) || 0
        : parseFloat(modal?.dataset.ticketPrice) || 0;
      update_product_amount(price * quantity);
    },
  });
}

function clearConfirmedState() {
  confirmedSelection = [];
  reservedIds = [];
  modalSelection = new Set();
  syncHiddenFields();
  renderConfirmedDisplay();
  update_product_amount(0);
}

async function releaseReservations(ids) {
  if (!ids.length) return;

  await $.ajax({
    url: "/raffle/reserve_numbers",
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken() },
    data: {
      campaign_id: campaignId(),
      numbers: [],
      previous_reserved_ids: ids,
    },
  });
}

async function fetchNumbersStatus() {
  const response = await $.get("/raffle/numbers_status", { campaign_id: campaignId() });
  blockedNumbers = new Set(response.blocked_numbers || []);
}

async function loadNumbersStatus() {
  await fetchNumbersStatus();
  if (gridBuilt) refreshBlockedState();
}

function removeOrphanModalBackdrop() {
  if ($(".modal.show").length === 0) {
    $("body").removeClass("modal-open").css("padding-right", "");
    $(".modal-backdrop").remove();
  }
}

function hideRaffleNumberModal() {
  return new Promise((resolve) => {
    const $modal = $("#raffle-number-selection");
    if (!$modal.hasClass("show")) {
      removeOrphanModalBackdrop();
      resolve();
      return;
    }
    $modal.one("hidden.bs.modal", () => {
      removeOrphanModalBackdrop();
      resolve();
    });
    $modal.modal("hide");
  });
}

function showRaffleNumberModal() {
  $("#raffle-number-selection").modal("show");
}

async function openSelectionModal() {
  const previousIds = parseReservedIds();

  clearConfirmedState();
  resetModalSelection();

  setGridLoading(true);
  $("#raffle-number-selection").modal("show");

  await Promise.all([
    previousIds.length ? releaseReservations(previousIds) : Promise.resolve(),
    fetchNumbersStatus(),
  ]);

  resetFiltersToDefault();
  await rebuildPool();
  renderModalSelectedChips();
}

async function finalizeSelection() {
  if (finalizeInFlight) return;
  finalizeInFlight = true;

  const finishBtn = document.getElementById("raffle-finish-selection-btn");
  let deferUnlock = false;

  try {
    const selection = currentModalSelection();
    if (selection.length === 0) {
      deferUnlock = true;
      return;
    }

    if (finishBtn) finishBtn.disabled = true;

    await hideRaffleNumberModal();

    const response = await $.ajax({
      url: "/raffle/reserve_numbers",
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
      data: {
        campaign_id: campaignId(),
        numbers: selection,
        previous_reserved_ids: parseReservedIds(),
      },
    });

    if (!response.success) {
      const unavailable = (response.unavailable_numbers || []).map((n) => formatNumberLabel(n)).join(", ");
      alert(`Los siguientes números ya no están disponibles: ${unavailable}`);
      const unavailableSet = new Set(response.unavailable_numbers || []);
      modalSelection = new Set(selection.filter((n) => !unavailableSet.has(n)));
      await loadNumbersStatus();
      renderModalSelectedChips();
      showRaffleNumberModal();
      return;
    }

    reservedIds = response.reserved_ids || [];
    confirmedSelection = [...(response.numbers || selection)].sort((a, b) => a - b);
    modalSelection = new Set();

    syncHiddenFields();
    renderConfirmedDisplay();
    updateConfirmedTotal();
  } finally {
    if (finishBtn) finishBtn.disabled = modalSelection.size === 0;
    if (deferUnlock) {
      setTimeout(() => {
        finalizeInFlight = false;
      }, 0);
    } else {
      finalizeInFlight = false;
    }
  }
}

async function removeConfirmedNumber(number) {
  const index = confirmedSelection.indexOf(number);
  if (index === -1) return;

  const idToRelease = reservedIds[index];
  if (idToRelease) {
    await releaseReservations([idToRelease]);
    reservedIds.splice(index, 1);
  }

  confirmedSelection.splice(index, 1);
  syncHiddenFields();
  renderConfirmedDisplay();
  updateConfirmedTotal();
}

function initRaffleNumberSelection() {
  if (initialized) return;
  if (!document.getElementById("raffle-number-selection")) return;

  initialized = true;

  $("#raffle-number-selection")
    .off("hidden.bs.modal.raffleNumberSelection")
    .on("hidden.bs.modal.raffleNumberSelection", removeOrphanModalBackdrop);

  $(document)
    .off("click.raffleNumberSelection", ".select-tickets-btn")
    .on("click.raffleNumberSelection", ".select-tickets-btn", (e) => {
      e.preventDefault();
      openSelectionModal();
    });

  $(document)
    .off("click.raffleNumberSelection", "#raffle-finish-selection-btn")
    .on("click.raffleNumberSelection", "#raffle-finish-selection-btn", (e) => {
      e.preventDefault();
      finalizeSelection();
    });

  $(document)
    .off("click.raffleNumberSelection", ".random-select-button")
    .on("click.raffleNumberSelection", ".random-select-button", (e) => {
      e.preventDefault();
      selectRandomTicket();
    });

  $(document)
    .off("change.raffleNumberSelection", 'input[name="raffle-filter-availability"]')
    .on("change.raffleNumberSelection", 'input[name="raffle-filter-availability"]', () => {
      syncDraftFromInputs();
    });

  $(document)
    .off("input.raffleNumberSelection", "#raffle-range-min")
    .on("input.raffleNumberSelection", "#raffle-range-min", onRangeMinInput);

  $(document)
    .off("input.raffleNumberSelection", "#raffle-range-max")
    .on("input.raffleNumberSelection", "#raffle-range-max", onRangeMaxInput);

  $(document)
    .off("click.raffleNumberSelection", "#raffle-apply-filters-btn")
    .on("click.raffleNumberSelection", "#raffle-apply-filters-btn", (e) => {
      e.preventDefault();
      applyFilters();
    });

  $(document)
    .off("click.raffleNumberSelection", "#raffle-clear-filters-btn")
    .on("click.raffleNumberSelection", "#raffle-clear-filters-btn", (e) => {
      e.preventDefault();
      clearFilters();
    });

  updateRangeSliderUI();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRaffleNumberSelection);
} else {
  initRaffleNumberSelection();
}
